import React from 'react'

import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import {
    Assignment as GuestCardIcon,
    Close as CloseIcon,
} from '@material-ui/icons'

import {
    ApartmentTourGuestCardResults,
    ApartmentTourLead,
} from '../../models/Outreach/types'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: 16,
            width: 'min(520px, 92vw)',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            margin: theme.spacing(2),
        },
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: theme.spacing(2.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#f9fafb',
        flexShrink: 0,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    titleIcon: {
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: '#ede9fe',
        color: '#7c3aed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
        marginTop: 4,
        marginLeft: 52,
    },
    dialogContent: {
        padding: theme.spacing(2.5, 3, 2),
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
    },
    section: {
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        fontSize: '0.65rem',
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    fieldRow: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 2,
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        marginBottom: theme.spacing(1),
        backgroundColor: '#fafafa',
        '&:last-child': {
            marginBottom: 0,
        },
    },
    fieldLabel: {
        fontSize: '0.7rem',
        fontWeight: 600,
        color: '#6b7280',
    },
    fieldValue: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '#1f2937',
        whiteSpace: 'pre-wrap' as const,
    },
    empty: {
        textAlign: 'center' as const,
        padding: theme.spacing(4, 2),
        color: theme.palette.text.secondary,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: theme.spacing(1.5, 2.5, 2),
    },
}))

const formatRent = (value: number | null | undefined) => {
    if (value == null) return null
    return `$${Number(value).toLocaleString()}`
}

const formatDate = (value: string | null | undefined) => {
    if (!value) return null
    const date = new Date(`${value}T00:00:00`)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

interface ViewGuestCardResultsDialogProps {
    open: boolean
    onClose: () => void
    lead: ApartmentTourLead | null
}

export const ViewGuestCardResultsDialog: React.FC<ViewGuestCardResultsDialogProps> = ({
    open,
    onClose,
    lead,
}) => {
    const classes = useStyles()
    const results: ApartmentTourGuestCardResults | null =
        lead?.guest_card_results ?? null

    const contactFields = [
        { label: 'Name', value: lead?.name ?? null },
        { label: 'Email', value: lead?.email ?? null },
        { label: 'Phone', value: lead?.phone ?? null },
    ].filter((field) => field.value)

    const preferenceFields = [
        {
            label: 'Preferred floor plan',
            value: results?.preferred_unit_config?.name ?? null,
        },
        {
            label: 'Preferred rent',
            value: formatRent(results?.preferred_rent_cost),
        },
        {
            label: 'Preferred move-in',
            value: formatDate(results?.preferred_move_in_date),
        },
        {
            label: 'Additional requests',
            value: results?.additional_requests ?? null,
        },
    ].filter((field) => field.value)

    const customAnswers = results?.custom_answers ?? []
    const hasPreferenceContent =
        preferenceFields.length > 0 || customAnswers.length > 0

    const renderField = (label: string, value: string) => (
        <Box key={label} className={classes.fieldRow}>
            <Typography className={classes.fieldLabel}>{label}</Typography>
            <Typography className={classes.fieldValue}>{value}</Typography>
        </Box>
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth={false}
        >
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Box>
                    <Box className={classes.titleRow}>
                        <span className={classes.titleIcon}>
                            <GuestCardIcon style={{ fontSize: 22 }} />
                        </span>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                            Guest Card Results
                        </Typography>
                    </Box>
                    <Typography className={classes.subtitle}>
                        {lead?.name
                            ? `Submitted for ${lead.name}`
                            : 'Submitted guest card'}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {contactFields.length > 0 && (
                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Contact
                        </Typography>
                        {contactFields.map((field) =>
                            renderField(field.label, field.value as string),
                        )}
                    </Box>
                )}

                {preferenceFields.length > 0 && (
                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Preferences
                        </Typography>
                        {preferenceFields.map((field) =>
                            renderField(field.label, field.value as string),
                        )}
                    </Box>
                )}

                {customAnswers.length > 0 && (
                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Custom answers
                        </Typography>
                        {customAnswers.map((answer) =>
                            renderField(answer.question, answer.answer || '—'),
                        )}
                    </Box>
                )}

                {!hasPreferenceContent && (
                    <Box className={classes.empty}>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            Guest card marked complete, but no preferences or
                            custom answers were recorded
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <Divider />
            <Box className={classes.footer}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    style={{
                        textTransform: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                    }}
                >
                    Close
                </Button>
            </Box>
        </Dialog>
    )
}
